import NiceModal from '@ebay/nice-modal-react';
import ApmProvider from '@gef-ui/apm/ApmProvider';
import { instrumentTypes, useBreadcrumbs } from '@gef-ui/apm/instruments';
import { AuthorizationProvider, useAuthClient, useLazyUserLogin } from '@gef-ui/authorization-v2';
import { Provider as ContextDefaultsProvider } from '@gef-ui/context-defaults';
import { ConfigJSONProvider, useConfig } from '@gef-ui/env-config';
import { ErrorProvider, ModalErrorProvider, RootErrorProvider, middlewareGlobalHandler, useErrorDispatch, useHandleConfigError, useHandleErrorLogout } from '@gef-ui/error-report-material';
import FullScreenError from '@gef-ui/error-report-material/components/FullScreenError';
import { getMessage, initDefaultMessages, loadMessageResources } from '@gef-ui/localization/messages';
import AppLoader from '@gef-ui/material/components/AppLoader';
import NotistackProvider from '@gef-ui/material/components/NotistackProvider';
import { cacheMiddleware } from '@gef-ui/middleware-api/middleware/cache';
import { oauth2MiddlewareAuthV2 } from '@gef-ui/middleware-api/middleware/oauth2';
import { reactiveApiMiddleware } from '@gef-ui/middleware-api/middleware/reactiveApi';
import { statusMiddleware } from '@gef-ui/middleware-api/middleware/status';
import { updateAccessToken } from '@gef-ui/redux-common/actions/appContext';
import urlsReducer from '@gef-ui/redux-common/reducers/urls';
import { stateManagementSlice } from '@gef-ui/statemanagement-redux';
import useIsStateReady from "@gef-ui/utils/useIsStateReady";
import { VersionInfoInitializer, VersionInfoLazyProvider } from '@gef-ui/version-info-core';
import CssBaseline from '@mui/material/CssBaseline';
import NoSsr from '@mui/material/NoSsr';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { cs } from 'date-fns/locale/cs';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useMemo } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { applyMiddleware, combineReducers, compose, legacy_createStore as reduxCreateStore } from 'redux';
import { enableBatching } from 'redux-batched-actions';
import { thunk } from 'redux-thunk';
import { setGlobalDevModeChecks } from 'reselect';
let globalReduxStore;
export const getReduxStore = ()=>globalReduxStore;
const ApmInstruments = ({ children, breadcrumbsInstruments, breadcrumbsOptions })=>{
    useBreadcrumbs(breadcrumbsInstruments, breadcrumbsOptions);
    return children;
};
const ApmIntegration = ({ enabled, excludedEnv = process.env.NODE_ENV === 'development', children, breadcrumbsInstruments, breadcrumbsOptions, ...other })=>{
    if (!enabled || excludedEnv) {
        return children;
    } else {
        return /*#__PURE__*/ React.createElement(ApmProvider, other, /*#__PURE__*/ React.createElement(ApmInstruments, {
            breadcrumbsInstruments: breadcrumbsInstruments,
            breadcrumbsOptions: breadcrumbsOptions
        }, children));
    }
};
ApmIntegration.propTypes = {
    breadcrumbsInstruments: PropTypes.array,
    breadcrumbsOptions: PropTypes.object,
    children: PropTypes.node,
    enabled: PropTypes.bool,
    excludedEnv: PropTypes.bool
};
const basicDefaults = {
    ...stateManagementSlice
};
export const globalInit = ({ localization, defaultLocale = 'cs' })=>{
    initDefaultMessages(defaultLocale);
    if (localization) {
        loadMessageResources(localization);
    }
};
export const applyMiddlewareWithDevTool = (middleware)=>{
    const composeEnhancers = typeof window === 'object' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ : compose;
    return composeEnhancers(applyMiddleware(...middleware));
};
const defaultCreateStore = ({ reducers, initState, config, middlewares, authClient, user, errorDispatch, middlewareGlobalErrorProps, includeAuthMiddleware })=>reduxCreateStore(enableBatching(combineReducers(reducers)), {
        ...initState,
        urls: {
            ...initState?.urls,
            ...config?.urls,
            apiUrl: config?.apiUrl
        }
    }, applyMiddlewareWithDevTool(middlewares(authClient, config, user, errorDispatch, middlewareGlobalErrorProps, includeAuthMiddleware)));
const defaultMiddlewares = (authClient, _config, getUser, errorDispatch, includeAuthMiddleware, middlewareGlobalErrorProps)=>[
        cacheMiddleware(),
        ...includeAuthMiddleware ? [
            oauth2MiddlewareAuthV2(undefined, authClient)
        ] : [],
        statusMiddleware(),
        reactiveApiMiddleware(),
        middlewareGlobalHandler({
            errorDispatch,
            getUser,
            ...middlewareGlobalErrorProps
        }),
        thunk
    ];
const defaultDevModeChecks = {
    identityFunctionCheck: 'never',
    inputStabilityCheck: 'never'
};
const ReduxBootstrap = ({ children, reducers = {
    urls: urlsReducer
}, initState = {}, middlewareGlobalErrorProps = {}, includeAuthMiddleware, createStore = defaultCreateStore, middlewares = defaultMiddlewares, devModeChecks = defaultDevModeChecks })=>{
    const authClient = useAuthClient();
    const errorDispatch = useErrorDispatch();
    const user = useLazyUserLogin();
    const config = useConfig();
    // biome-ignore lint/correctness/useExhaustiveDependencies: non-production-code
    const store = useMemo(()=>{
        globalReduxStore = createStore({
            reducers,
            initState,
            config,
            middlewares,
            authClient,
            user,
            errorDispatch,
            middlewareGlobalErrorProps,
            includeAuthMiddleware
        });
        return globalReduxStore;
    }, // ensure that store is created only once
    []);
    useEffect(()=>{
        if (authClient) {
            const unsubscribe = authClient.on('token', (token)=>{
                store.dispatch(updateAccessToken(token));
            });
            return ()=>{
                unsubscribe();
            };
        }
    }, [
        authClient,
        store
    ]);
    useEffect(()=>{
        setGlobalDevModeChecks(devModeChecks);
    }, [
        devModeChecks
    ]);
    return /*#__PURE__*/ React.createElement(ReduxProvider, {
        store: store
    }, children);
};
ReduxBootstrap.propTypes = {
    children: PropTypes.node,
    createStore: PropTypes.func,
    includeAuthMiddleware: PropTypes.bool,
    initState: PropTypes.object,
    middlewareGlobalErrorProps: PropTypes.object,
    middlewares: PropTypes.func,
    reducers: PropTypes.object,
    devModeChecks: PropTypes.object
};
const configJSONPropsDefaults = {
    loader: /*#__PURE__*/ React.createElement(AppLoader, null),
    useHandleConfigError
};
const authorizationPropsDefaults = {
    loader: /*#__PURE__*/ React.createElement(AppLoader, null),
    useHandleErrorLogout
};
const apmPropsDefaults = {
    enabled: false,
    excludedEnv: process.env.NODE_ENV === 'development',
    breadcrumbsInstruments: [
        instrumentTypes.DOM,
        instrumentTypes.FORM,
        instrumentTypes.XHR,
        instrumentTypes.FETCH,
        instrumentTypes.REDUX
    ]
};
const localizationProviderPropsDefaults = {
    dateAdapter: AdapterDateFns,
    adapterLocale: cs
};
const Bootstrap = ({ theme, defaults = {}, children, notistackProps = {
    maxSnack: 3
}, niceModalProps = {}, NiceModalComponent = NiceModal.Provider, authorizationProps = {}, reduxProps = {}, configJSONProps = {}, versionInfoInitializerProps = {}, apmProps = {}, includeAuthProvider = true, validateConfigParams = includeAuthProvider, localizationProviderProps = {}, themeProviderProps = {}, waitForState = false, waitForStateErrorMessage = getMessage('common:missingStateParameter') })=>{
    const { isLoaded, isError } = useIsStateReady(waitForState, defaults?.routing?.Router?.useRouteChanged);
    const [AuthProvider, authProviderProps] = includeAuthProvider ? [
        AuthorizationProvider,
        {
            ...authorizationPropsDefaults,
            ...authorizationProps
        }
    ] : [
        Fragment,
        {}
    ];
    const isExternalTheme = theme.isExternal;
    return /*#__PURE__*/ React.createElement(VersionInfoLazyProvider, null, /*#__PURE__*/ React.createElement(StyledEngineProvider, {
        injectFirst: true
    }, /*#__PURE__*/ React.createElement(ThemeProvider, {
        theme: theme,
        ...themeProviderProps
    }, /*#__PURE__*/ React.createElement(NoSsr, null, /*#__PURE__*/ React.createElement(CssBaseline, null)), /*#__PURE__*/ React.createElement(RootErrorProvider, null, /*#__PURE__*/ React.createElement(ConfigJSONProvider, {
        ...configJSONPropsDefaults,
        ...configJSONProps,
        shouldValidate: validateConfigParams
    }, /*#__PURE__*/ React.createElement(ContextDefaultsProvider, {
        value: {
            ...basicDefaults,
            ...defaults
        }
    }, /*#__PURE__*/ React.createElement(AuthProvider, authProviderProps, /*#__PURE__*/ React.createElement(ErrorProvider, {
        hideModalProvider: true
    }, /*#__PURE__*/ React.createElement(ReduxBootstrap, {
        ...reduxProps,
        includeAuthMiddleware: includeAuthProvider
    }, /*#__PURE__*/ React.createElement(LocalizationProvider, {
        ...localizationProviderPropsDefaults,
        ...localizationProviderProps
    }, /*#__PURE__*/ React.createElement(VersionInfoInitializer, versionInfoInitializerProps), /*#__PURE__*/ React.createElement(NotistackProvider, notistackProps, /*#__PURE__*/ React.createElement(NiceModalComponent, niceModalProps, /*#__PURE__*/ React.createElement(ApmIntegration, {
        ...apmPropsDefaults,
        ...apmProps
    }, /*#__PURE__*/ React.createElement(ModalErrorProvider, {
        withCloseButtonSingle: true
    }), isError ? /*#__PURE__*/ React.createElement(FullScreenError, {
        error: {
            heading: isExternalTheme ? undefined : waitForStateErrorMessage,
            shortDescription: isExternalTheme ? waitForStateErrorMessage : undefined
        }
    }) : isLoaded ? children : /*#__PURE__*/ React.createElement(AppLoader, null))))))))))))));
};
Bootstrap.propTypes = {
    NiceModalComponent: PropTypes.elementType,
    apmProps: PropTypes.object,
    authorizationProps: PropTypes.object,
    children: PropTypes.node,
    configJSONProps: PropTypes.object,
    defaults: PropTypes.object,
    includeAuthProvider: PropTypes.bool,
    localizationProviderProps: PropTypes.object,
    niceModalProps: PropTypes.object,
    notistackProps: PropTypes.object,
    reduxProps: PropTypes.object,
    theme: PropTypes.object,
    validateConfigParams: PropTypes.bool,
    versionInfoInitializerProps: PropTypes.object,
    themeProviderProps: PropTypes.object,
    waitForState: PropTypes.bool,
    waitForStateErrorMessage: PropTypes.string
};
export default Bootstrap;
